import React, { useRef, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { ChatMessage, ProposedPlan, AgentRun } from "../../api/agent";
import AgentProgress from "./AgentProgress";
import s from "./AgentChat.module.css";
import styles from "./ChatInterface.module.css";

const STEP_TYPES = new Set(["THINKING", "TOOL_CALL", "TOOL_RESULT"]);

function Bubble({ msg }: { msg: ChatMessage }) {
  const labels: Record<string, string> = {
    user: "You",
    AGENT: "Assistant",
    ERROR: "❌ Error",
  };
  const isUser = msg.type === "user";
  return (
    <div
      className={`${s.bubble} ${s[`bubble_${msg.type}`]} ${isUser ? s.bubbleUser : ""} ${styles.messageIn}`}
    >
      <span className={s.label}>{labels[msg.type] || msg.type}</span>
      {msg.type === "AGENT" ? (
        <div className={`${s.text} ${s.markdown}`}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({ href, children }) => (
                <a href={href} target="_blank" rel="noopener noreferrer">
                  {children}
                </a>
              ),
            }}
          >
            {msg.content}
          </ReactMarkdown>
        </div>
      ) : (
        <p className={s.text}>{msg.content}</p>
      )}
    </div>
  );
}

function StepsGroup({ steps }: { steps: ChatMessage[] }) {
  const [open, setOpen] = useState(false);
  const labels: Record<string, string> = {
    THINKING: "🤔 Thinking",
    TOOL_CALL: "🔧 Tool",
    TOOL_RESULT: "📋 Result",
  };
  return (
    <div>
      <button className={s.stepsToggle} onClick={() => setOpen((o) => !o)}>
        <span className={`${s.stepsArrow} ${open ? s.stepsArrowOpen : ""}`}>
          ▶
        </span>
        View activity · {steps.length} event{steps.length > 1 ? "s" : ""}
      </button>
      {open && (
        <div className={s.stepsContainer}>
          {steps.map((step) => (
            <div
              key={step.id}
              className={`${s.stepBubble} ${s[`step_${step.type}`]}`}
            >
              <span className={s.stepLabel}>
                {labels[step.type] || step.type}
                {step.metadata?.tool_name
                  ? ` — ${step.metadata.tool_name}`
                  : ""}
              </span>
              <p className={s.stepText}>
                {step.content ||
                  (step.type === "TOOL_CALL" && step.metadata?.tool_input
                    ? JSON.stringify(step.metadata.tool_input, null, 2)
                    : step.type === "TOOL_RESULT" &&
                        step.metadata?.tool_output != null
                      ? typeof step.metadata.tool_output === "string"
                        ? step.metadata.tool_output
                        : JSON.stringify(step.metadata.tool_output, null, 2)
                      : "")}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function groupMessages(messages: ChatMessage[]) {
  const groups: Array<
    { kind: "msg"; msg: ChatMessage } | { kind: "steps"; steps: ChatMessage[] }
  > = [];
  let buf: ChatMessage[] = [];
  const flush = () => {
    if (buf.length) {
      groups.push({ kind: "steps", steps: buf });
      buf = [];
    }
  };
  for (const m of messages) {
    if (STEP_TYPES.has(m.type)) {
      buf.push(m);
    } else {
      flush();
      groups.push({ kind: "msg", msg: m });
    }
  }
  flush();
  return groups;
}

const SERVICE_ICONS: Record<string, string> = {
  analytics: "📊",
  monitoring: "👀",
  briefing: "📝",
  similarity: "🔍",
  clustering: "🧩",
  wordcloud: "☁️",
};

function PlanCard({
  plan,
  onApprove,
  onReject,
  disabled,
}: {
  plan: ProposedPlan;
  onApprove: () => void;
  onReject: () => void;
  disabled: boolean;
}) {
  return (
    <div className={s.planCard}>
      <div className={s.planHeader}>
        <span className={s.planTitle}>📋 Execution Plan</span>
        {plan.total_estimated_credits == null ? (
          <span className={s.creditBadgePaid}>
            💳 Credit cost not yet known
          </span>
        ) : plan.total_estimated_credits > 0 ? (
          <span className={s.creditBadgePaid}>
            💳 {plan.total_estimated_credits} credit
            {plan.total_estimated_credits !== 1 ? "s" : ""}
          </span>
        ) : (
          <span className={s.creditBadgeFree}>✓ Free</span>
        )}
      </div>
      {plan.reasoning && <p className={s.hint}>{plan.reasoning}</p>}
      <ul className={s.planSteps}>
        {plan.steps.map((step) => (
          <li key={step.step_number} className={s.planStep}>
            <span className={s.planStepNum}>{step.step_number}</span>
            <span className={s.planStepDesc}>{step.description}</span>
            <span
              className={`${s.planServiceBadge} ${s[`svc_${step.service}`] || ""}`}
            >
              {SERVICE_ICONS[step.service] || "⚙️"} {step.service}
            </span>
          </li>
        ))}
      </ul>
      <div className={s.planActions}>
        <button
          className={s.planRejectBtn}
          onClick={onReject}
          disabled={disabled}
        >
          Cancel
        </button>
        <button
          className={s.planApproveBtn}
          onClick={onApprove}
          disabled={disabled}
        >
          Run Plan →
        </button>
      </div>
      {disabled && (
        <p className={s.hint} role="status">
          Reconnect the assistant to review this plan.
        </p>
      )}
    </div>
  );
}

export interface ChatInterfaceProps {
  messages: ChatMessage[];
  isThinking: boolean;
  error: string | null;
  pendingPlan: ProposedPlan | null;
  onSend: (content: string) => void | Promise<boolean>;
  onApprove: () => void;
  onReject: () => void;
  className?: string;
  emptyState?: React.ReactNode;
  inputPlaceholder?: string;
  embedded?: boolean;
  planActionsDisabled?: boolean;
  activeRun?: AgentRun | null;
  canStop?: boolean;
  onStop?: () => void;
  isConnected?: boolean;
  focused?: boolean;
  conversationKey?: string;
  unsentRequest?: string | null;
  onClearUnsentRequest?: () => void;
  draft?: { value: string; onChange: (value: string) => void };
}

export default function ChatInterface({
  messages,
  isThinking,
  error,
  pendingPlan,
  onSend,
  onApprove,
  onReject,
  className,
  emptyState,
  inputPlaceholder,
  embedded = false,
  planActionsDisabled = false,
  draft,
  activeRun,
  canStop,
  onStop,
  isConnected,
  focused = false,
  conversationKey,
  unsentRequest,
  onClearUnsentRequest,
}: ChatInterfaceProps) {
  const [failedDraft, setFailedDraft] = useState("");
  const [localInput, setLocalInput] = useState("");
  const input = draft?.value ?? localInput;
  const recoverableRequest = unsentRequest || failedDraft;
  const setInput = draft?.onChange ?? setLocalInput;
  const bottomRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const nearBottom = useRef(true);
  const conversationRef = useRef(conversationKey);
  conversationRef.current = conversationKey;
  useEffect(() => {
    nearBottom.current = true;
    setNewActivity(false);
    setFailedDraft("");
  }, [conversationKey]);
  const [newActivity, setNewActivity] = useState(false);
  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    if (nearBottom.current) {
      body.scrollTop = body.scrollHeight;
      setNewActivity(false);
    } else setNewActivity(true);
  }, [messages, embedded, conversationKey]);
  const blocked =
    isThinking ||
    !!pendingPlan ||
    activeRun?.status === "running" ||
    activeRun?.status === "stopping" ||
    activeRun?.status === "awaiting_approval";

  const handleSend = () => {
    const t = input.trim();
    if (!t || blocked) return;
    setInput("");
    setFailedDraft("");
    const sentFor = conversationRef.current;
    const sent = onSend(t);
    if (sent && typeof sent.then === "function")
      void sent
        .then((ok) => {
          if (ok === false && sentFor === conversationRef.current)
            setFailedDraft(t);
        })
        .catch(() => {
          if (sentFor === conversationRef.current) setFailedDraft(t);
        });
  };

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      className={`${styles.container} ${focused ? styles.focused : ""} ${className ?? ""}`}
    >
      <div
        className={s.body}
        ref={bodyRef}
        onScroll={() => {
          const body = bodyRef.current;
          if (body) {
            nearBottom.current =
              body.scrollHeight - body.scrollTop - body.clientHeight < 70;
            if (nearBottom.current) setNewActivity(false);
          }
        }}
        aria-live="polite"
        aria-label="Assistant conversation"
      >
        {messages.length === 0 &&
          (emptyState !== undefined ? (
            emptyState
          ) : (
            <div className={s.empty}>
              <h2>What would you like to work on?</h2>
              <p>
                Review a website, explore an idea, or plan your next article.
              </p>
              {focused && (
                <div className={styles.starters}>
                  {[
                    "Review my website",
                    "Find my next article",
                    "Create a content brief",
                  ].map((prompt) => (
                    <button
                      key={prompt}
                      type="button"
                      onClick={() => {
                        setInput(prompt);
                        bodyRef.current?.parentElement
                          ?.querySelector("textarea")
                          ?.focus();
                      }}
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        {groupMessages(messages).map((g, i) =>
          g.kind === "steps" ? (
            <StepsGroup key={`steps-${i}`} steps={g.steps} />
          ) : (
            <Bubble key={g.msg.id} msg={g.msg} />
          ),
        )}
        {error && <p className={s.error}>{error}</p>}
        <div ref={bottomRef} />
      </div>

      {newActivity && (
        <button
          type="button"
          className={styles.newActivity}
          onClick={() => {
            const body = bodyRef.current;
            if (body) body.scrollTop = body.scrollHeight;
            nearBottom.current = true;
            setNewActivity(false);
          }}
        >
          New activity ↓
        </button>
      )}
      <AgentProgress
        run={activeRun}
        messages={messages}
        connected={isConnected}
        onStop={onStop}
        canStop={canStop}
        isThinking={isThinking}
      />
      {pendingPlan && (
        <PlanCard
          plan={pendingPlan}
          onApprove={onApprove}
          onReject={onReject}
          disabled={planActionsDisabled}
        />
      )}

      {recoverableRequest && (
        <div className={styles.composerHint} role="alert">
          Your request was not sent.{" "}
          <button
            type="button"
            onClick={() => {
              setInput(recoverableRequest + (input ? "\n\n" + input : ""));
              setFailedDraft("");
              onClearUnsentRequest?.();
            }}
          >
            Restore unsent request
          </button>
        </div>
      )}
      {blocked && (
        <p className={styles.composerHint}>
          {pendingPlan || activeRun?.status === "awaiting_approval"
            ? "Review the plan to continue. You can keep drafting."
            : "You can keep drafting. Send is available when this run ends."}
        </p>
      )}
      <div className={s.inputBar}>
        <textarea
          className={s.input}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKey}
          placeholder={
            pendingPlan
              ? "Approve or cancel the plan above…"
              : inputPlaceholder || "Ask anything… (Enter to send)"
          }
          aria-label="Message your assistant"
          rows={2}
        />
        <button
          className={s.sendBtn}
          onClick={handleSend}
          aria-label="Send message"
          disabled={!input.trim() || blocked}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
          </svg>
        </button>
      </div>
    </div>
  );
}
